import React, { useEffect, useRef, useState } from 'react';
import type { ProgressStage } from '@privisy/api-types';
import { T, SECTION_META } from '../designTokens';

interface ScanProgressProps {
  stage: ProgressStage;
  message: string;
  percentage: number;
  /** URL currently being scanned (shown in the header when provided). */
  url?: string | null;
  /** Batch position when scanning multiple URLs. */
  batch?: { current: number; total: number } | null;
}

// ---------------------------------------------------------------------------
// Stage rail definition — mirrors the report's numbered sections (SECTION_META)
// with a discovery step before and a scoring step after, so the in-progress
// view reads like the audit document it produces.
// ---------------------------------------------------------------------------

interface RailStep {
  number: string;
  label: string;
  caption: string;
  /**
   * Editorial, not a citation mirror (issue #776's ratified decision): each
   * step covers several catalog checks (e.g. the uiCompliance step below
   * rolls up both the Do-Not-Sell-Link and Symmetry-of-Choice checks into
   * one line), so this is deliberately a coarser per-stage summary, not a
   * per-check reference. It does not derive from `CheckCatalogEntry.citationIds`
   * and is exempt from the catalog/CHECK_CITATION_MAP parity checks in
   * `www/src/lib/regulations/citation-map-parity.test.ts`.
   */
  ccpaRef?: string;
}

const RAIL_STEPS: RailStep[] = [
  {
    number: '00',
    label: 'Asset Discovery',
    caption: 'Loading the page, expanding hidden sections, capturing every network call',
  },
  {
    number: SECTION_META.uiCompliance.number,
    label: SECTION_META.uiCompliance.label,
    caption: 'Consent banner symmetry, "Do Not Sell or Share" link, dark-pattern indicators',
    ccpaRef: '§ 1798.135 · § 7004',
  },
  {
    number: SECTION_META.trackerDetection.number,
    label: SECTION_META.trackerDetection.label,
    caption: 'Intercepting third-party requests across 7 tracker categories, inspecting payloads for PII',
    ccpaRef: '§ 1798.140(ad)',
  },
  {
    number: SECTION_META.gpcValidation.number,
    label: SECTION_META.gpcValidation.label,
    caption: 'Re-visiting with Global Privacy Control enabled to verify the opt-out signal is honored',
    ccpaRef: '§ 7025',
  },
  {
    number: SECTION_META.privacyPolicy.number,
    label: SECTION_META.privacyPolicy.label,
    caption: 'Locating the privacy policy and checking each required CCPA/CPRA disclosure',
    ccpaRef: '§ 7011',
  },
  {
    number: '05',
    label: 'Scoring & Report',
    caption: 'Weighing every check, assembling evidence, and calculating the compliance score',
  },
];

// SSE stage keys → rail step index. Keys come from the scanner orchestrator's
// emitProgress calls; unknown keys fall back to percentage thresholds.
// Typed against ProgressStage (@privisy/api-types) so a typo'd key is a
// compile error instead of a silently-skipped rail step (issue #779).
// Deliberately partial: 'complete'/'error' are handled via isComplete/isError
// above, not this table, so percentage-based fallback is correct for them.
const STAGE_TO_STEP: Partial<Record<ProgressStage, number>> = {
  initializing: 0,
  loading: 0,
  loaded: 0,
  exploring: 0,
  'exploration-complete': 0,
  'ui-check': 1,
  'ui-complete': 1,
  'spi-detection': 1,
  'spi-complete': 1,
  'form-pages': 1,
  trackers: 2,
  'trackers-complete': 2,
  gpc: 3,
  'gpc-complete': 3,
  'consent-sim': 3,
  'consent-sim-complete': 3,
  policy: 4,
  'policy-complete': 4,
  crawling: 5,
  credits_exhausted: 5,
  'network-trace': 5,
  'network-trace-complete': 5,
  'payload-analysis': 5,
  finalizing: 5,
};

function stepFromPercentage(pct: number): number {
  if (pct < 30) return 0;
  if (pct < 50) return 1;
  if (pct < 70) return 2;
  if (pct < 85) return 3;
  if (pct < 90) return 4;
  return 5;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type StepStatus = 'queued' | 'running' | 'done';

const STEP_BADGE: Record<StepStatus, { label: string; color: string; bg: string; border: string }> = {
  queued: { label: 'QUEUED', color: T.gray400, bg: T.gray50, border: T.gray200 },
  running: { label: 'RUNNING', color: T.cobalt600, bg: T.cobalt50, border: T.cobalt200 },
  done: { label: 'DONE', color: T.success, bg: T.successBg, border: T.successBorder },
};

export default function ScanProgress({ stage, message, percentage, url, batch }: ScanProgressProps) {
  // Some progress streams (e.g. the policy-scan SSE "Connected to progress
  // stream" event) emit a frame with no numeric percentage. Raw `undefined`
  // breaks every downstream calc here: `undefined < 100` is false (so the
  // heading flips to "Scan Complete!" prematurely), `width: undefined%` is
  // invalid CSS (so the bar renders full-width), and `{undefined}%` prints a
  // bare "%". Normalize to a clamped finite number so the UI stays coherent
  // regardless of what the caller passes.
  const pct = Number.isFinite(percentage) ? Math.max(0, Math.min(100, percentage)) : 0;
  const isComplete = stage === 'complete' || pct >= 100;
  const isError = stage === 'error';

  // ── Elapsed timer ────────────────────────────────────────────────────────
  const startRef = useRef<number>(Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (isComplete || isError) return;
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1_000);
    return () => clearInterval(timer);
  }, [isComplete, isError]);

  // ── Live audit log — evidence visible by default, even mid-scan ─────────
  const [log, setLog] = useState<{ at: number; text: string }[]>([]);
  useEffect(() => {
    if (!message) return;
    setLog((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].text === message) return prev;
      const at = Math.floor((Date.now() - startRef.current) / 1000);
      return [...prev.slice(-5), { at, text: message }];
    });
  }, [message]);

  // `stage` is typed against the shared wire contract (issue #779); a value
  // absent from STAGE_TO_STEP (e.g. 'complete'/'error', handled above, or a
  // stage this table deliberately doesn't map) falls back to the percentage
  // heuristic below.
  const activeStep = STAGE_TO_STEP[stage] ?? stepFromPercentage(pct);

  const stepStatus = (index: number): StepStatus => {
    if (isComplete) return 'done';
    if (index < activeStep) return 'done';
    if (index === activeStep && !isError) return 'running';
    return 'queued';
  };

  const stripeColor = (status: StepStatus): string => {
    if (status === 'done') return T.success;
    if (status === 'running') return T.cobalt600;
    return T.gray200;
  };

  const heading = isError ? 'Scan Interrupted' : isComplete ? 'Scan Complete!' : 'Scanning in Progress...';
  const dotColor = isError ? T.error : isComplete ? T.success : T.cobalt600;

  return (
    <div
      style={{
        backgroundColor: T.white,
        borderRadius: T.radiusLg,
        border: `1px solid ${T.gray200}`,
        boxShadow: T.shadowSm,
        padding: '24px 28px',
        marginBottom: '24px',
      }}
    >
      {/* ── Header: overline, heading + URL, elapsed/percentage readout ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span
              className={isComplete || isError ? undefined : 'scan-live-dot'}
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: T.radiusFull,
                backgroundColor: dotColor,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: T.fontMono,
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: T.gray400,
              }}
            >
              LIVE COMPLIANCE AUDIT
              {batch && batch.total > 1 ? ` · URL ${batch.current} OF ${batch.total}` : ''}
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              color: T.gray800,
              fontSize: '20px',
              fontWeight: 600,
              fontFamily: T.fontDisplay,
              letterSpacing: '-0.01em',
            }}
          >
            {heading}
          </h2>
          {url && (
            <div
              style={{
                fontFamily: T.fontMono,
                fontSize: '13px',
                color: T.gray500,
                marginTop: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '420px',
              }}
            >
              {url}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: '26px',
              fontWeight: 700,
              color: isError ? T.error : isComplete ? T.success : T.gray800,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {pct}%
          </div>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '0.08em',
              color: T.gray400,
              marginTop: '2px',
            }}
          >
            ELAPSED {formatElapsed(elapsed)}
          </div>
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: T.gray200,
          borderRadius: T.radiusFull,
          overflow: 'hidden',
          margin: '18px 0 20px',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            transform: `scaleX(${pct / 100})`,
            transformOrigin: 'left',
            backgroundColor: isError ? T.error : isComplete ? T.success : T.cobalt600,
            transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      </div>

      {/* ── Numbered stage rail — left-edge stripes form a live heatmap ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {RAIL_STEPS.map((step, index) => {
          const status = stepStatus(index);
          const isActive = status === 'running';
          return (
            <div
              key={step.number}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '10px 12px',
                borderLeft: `2px solid ${stripeColor(status)}`,
                borderRadius: T.radiusMd,
                backgroundColor: isActive ? T.cobalt50 : 'transparent',
                transition: 'background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <span
                style={{
                  fontFamily: T.fontMono,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: status === 'done' ? T.success : isActive ? T.cobalt600 : T.gray400,
                  paddingTop: '1px',
                }}
              >
                {step.number}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: T.fontBody,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: status === 'queued' ? T.gray400 : T.gray700,
                  }}
                >
                  {step.label}
                </div>
                {isActive && (
                  <div style={{ fontSize: '13px', color: T.gray500, marginTop: '2px', lineHeight: 1.5 }}>
                    {step.caption}
                    {step.ccpaRef && (
                      <span style={{ fontFamily: T.fontMono, fontSize: '12px', color: T.gray400 }}>
                        {' '}· {step.ccpaRef}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontFamily: T.fontMono,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  color: STEP_BADGE[status].color,
                  backgroundColor: STEP_BADGE[status].bg,
                  border: `1px solid ${STEP_BADGE[status].border}`,
                  borderRadius: T.radiusSm,
                  padding: '3px 8px',
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              >
                {STEP_BADGE[status].label}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Live audit log — the scanner shows its work as it happens ── */}
      {log.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            padding: '12px 14px',
            backgroundColor: T.gray50,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radiusMd,
          }}
        >
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: T.gray400,
              marginBottom: '8px',
            }}
          >
            AUDIT LOG
          </div>
          {log.map((entry, i) => (
            <div
              key={`${entry.at}-${i}`}
              style={{
                fontFamily: T.fontMono,
                fontSize: '12px',
                color: i === log.length - 1 ? T.gray600 : T.gray400,
                lineHeight: 1.7,
                display: 'flex',
                gap: '10px',
              }}
            >
              <span style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{formatElapsed(entry.at)}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
